import { z } from "zod";
import { cn } from "@/shared/lib/utils";
import { Badge, Skeleton } from "@/shared/components";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const ChannelCardSchema = z.object({
  name: z.string(),
  type: z.string(),
  url: z.string().optional(),
  thumbnail: z.string().optional(),
  metrics: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
});

type ChannelCardData = z.infer<typeof ChannelCardSchema>;

/* ────────────────────── Helpers ────────────────────── */

/** 채널 타입 → Badge color 매핑 */
function channelTypeToBadgeColor(
  type: string,
): "blue" | "emerald" | "violet" | "amber" | "teal" | "sky" {
  const t = type.toLowerCase();
  if (t.includes("블로그") || t.includes("blog")) return "blue";
  if (t.includes("카페") || t.includes("cafe")) return "emerald";
  if (t.includes("유튜브") || t.includes("youtube")) return "violet";
  if (t.includes("인스타") || t.includes("instagram")) return "amber";
  if (t.includes("스마트") || t.includes("smart")) return "teal";
  return "sky";
}

/** 이름에서 이니셜 추출 (최대 2자) */
function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

/* ────────────────────── Component ────────────────────── */

export function ChannelCard({
  widget,
  className,
}: AgentWidgetProps<ChannelCardData>) {
  const { name, type, url, thumbnail, metrics } = widget.data;
  const badgeColor = channelTypeToBadgeColor(type);

  return (
    <div className={cn("w-full", className)}>
      {/* 헤더: 아바타 + 이름 + 배지 */}
      <div className="flex items-center gap-3">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={name}
            className="size-11 shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-accent/10 text-[13px] font-semibold text-text-brand">
            {getInitials(name)}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-text-primary">
              {name}
            </p>
            <Badge variant="pill" color={badgeColor}>
              {type}
            </Badge>
          </div>
          {url && (
            <p className="mt-0.5 truncate text-[11px] text-text-tertiary">
              {url}
            </p>
          )}
        </div>
      </div>

      {/* 메트릭 */}
      {metrics.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {metrics.map((m, idx) => (
            <div
              key={idx}
              className="rounded-lg bg-surface-secondary/40 px-3 py-2 text-center"
            >
              <p className="text-[10px] text-text-tertiary">{m.label}</p>
              <p className="mt-0.5 text-[13px] font-semibold tabular-nums text-text-primary">
                {m.value}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function ChannelCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-xl border border-stroke-subtle bg-surface-raised p-4",
        className,
      )}
    >
      {/* 헤더 */}
      <div className="flex items-center gap-3">
        <Skeleton className="size-11 shrink-0 rounded-full" />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-14 rounded-full" />
          </div>
          <Skeleton className="h-3 w-36" />
        </div>
      </div>
      {/* 메트릭 그리드 */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        {Array.from({ length: 3 }, (_, i) => (
          <div
            key={i}
            className="rounded-lg bg-surface-secondary/40 px-3 py-2 text-center"
          >
            <Skeleton className="mx-auto h-2.5 w-10" />
            <Skeleton className="mx-auto mt-1.5 h-4 w-14" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────── Widget Definition ────────────────────── */

const channelCardWidget = defineWidget<ChannelCardData>({
  type: WIDGET_TYPES.CHANNEL_CARD,
  label: "채널 프로필 카드",
  schema: ChannelCardSchema,
  component: ChannelCard,
  skeleton: ChannelCardSkeleton,
  defaultLayout: "half",
  maxHeight: 180,
  aiHint: "채널 프로필 (이름, 타입, URL, 주요 메트릭)",
});

registerWidget(channelCardWidget);
